import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App.jsx";
import { BrowserRouter } from "react-router-dom";
import { installErrorReporter } from "./lib/errorReporter.js";
import ErrorBoundary from "./components/ErrorBoundary.jsx";

// Always install — captures crashes before/after auth, including PREVIEW.
installErrorReporter();

// @telegram-apps/analytics v1.6.4 has a first-load bug: somewhere inside
// its setInterval flush loop it does `[...JSON.parse(localStorage.getItem(k))]`
// where the key hasn't been written yet, so it spreads null and throws —
// which on Telegram WebView shows up as a white-screen on /login.
//
// Three layers of defense before re-enabling (Telegram review now requires it):
//   1. Pre-seed every localStorage key the SDK is known to touch with "[]"
//      so any `[...JSON.parse(getItem(k))]` finds an empty array.
//   2. Defer import to after first paint, so even if 1 misses a key, the
//      login page is already on screen before the SDK runs.
//   3. Wrap init + everything analytics-related in try/catch so a thrown
//      error in the SDK can't propagate up to React.
const ANALYTICS_TOKEN =
  "eyJhcHBfbmFtZSI6InNheWhpIiwiYXBwX3VybCI6Imh0dHBzOi8vdC5tZS9XaGlzcGVyUm9vbV9ib3QiLCJhcHBfZG9tYWluIjoiaHR0cHM6Ly9zYXloaWFwcC50b3AvIn0=!AUnLpmd0rbnb3o6jOtgseMmkQHlcT8fLLdt2JWuOMKU=";
try {
  // Keys observed in the SDK's minified output + common defensive picks.
  const seedKeys = [
    "payload",
    "tga_payload",
    "tga_events",
    "tga_queue",
    "events",
    "analytics_events",
  ];
  for (const k of seedKeys) {
    if (localStorage.getItem(k) == null) localStorage.setItem(k, "[]");
  }
} catch {
  // localStorage can throw in private-browsing / quota cases. Analytics
  // would fail anyway; not our problem.
}
// Defer to after first paint so the login page renders first regardless of
// what the SDK does.
setTimeout(() => {
  import("@telegram-apps/analytics")
    .then(({ default: telegramAnalytics }) => {
      try {
        telegramAnalytics.init({
          token: ANALYTICS_TOKEN,
          appName: "sayhi",
        });
      } catch (e) {
        console.warn("[analytics] init failed (non-fatal):", e?.message);
      }
    })
    .catch((e) =>
      console.warn(
        "[analytics] dynamic import failed (non-fatal):",
        e?.message
      )
    );
}, 300);

// Clear the chunk-reload guard once the app successfully boots, so the
// next failed import (after the *next* deploy) is allowed to reload again.
window.addEventListener("load", () => {
  setTimeout(() => sessionStorage.removeItem("sayhi:chunkReloaded"), 2000);
});

createRoot(document.getElementById("root")).render(
  <StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>
);
